/** @jsxImportSource @emotion/react */
'use memo'

import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { RegistrationSetupContextWrapper } from './context'
import RegistrationSetupContent from './RegistrationSetupContent'
import { Registration } from '@sportninja/common/types'
import { RegistrationTypes, Tax } from '@sportninja/common/types/Registration'
import { useModalScroll } from 'src/hooks/useModalScroll'

const TRANSITION_DURATION = 300

type RegistrationSetupProps = {
  isRegistrationFormOpen: boolean
  setIsRegistrationFormOpen: (isRegistrationFormOpen: boolean) => void
  registration: Registration | null
  registrationTypes: RegistrationTypes
  availableTaxes: Tax[]
  scheduleId: string
  refetchRegistrationFn: React.MutableRefObject<() => void>
  refetchRegistrationInfoFn: React.MutableRefObject<
    (displayLoading?: boolean) => void
  >
  accountId: string
}

const RegistrationSetup = ({
  isRegistrationFormOpen,
  setIsRegistrationFormOpen,
  registration,
  registrationTypes,
  availableTaxes,
  scheduleId,
  refetchRegistrationFn,
  refetchRegistrationInfoFn,
  accountId,
}: RegistrationSetupProps) => {
  const { openModal, closeModal } = useModalScroll()
  const location = useLocation()
  const openedAtPath = useRef<string | null>(null)

  const registrationVersion =
    registration?.root_schedule?.registration_version ?? 0

  const [isMounted, setIsMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isRegistrationFormOpen) {
      if (refetchRegistrationFn?.current) {
        refetchRegistrationFn.current()
      }
      openModal()
      setIsMounted(true)
      // Trigger enter animation on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true)
        })
      })
    } else {
      // Trigger exit animation, then unmount
      setIsVisible(false)
      const timer = setTimeout(() => {
        setIsMounted(false)
        closeModal()
      }, TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isRegistrationFormOpen, openModal, closeModal, refetchRegistrationFn])

  // Clean up the registration setup when the user navigates away from the page.
  useEffect(() => {
    if (isRegistrationFormOpen && openedAtPath.current === null) {
      openedAtPath.current = location.pathname
      return
    }
    if (!isRegistrationFormOpen) {
      openedAtPath.current = null
      return
    }
    if (
      openedAtPath.current !== null &&
      openedAtPath.current !== location.pathname
    ) {
      setIsRegistrationFormOpen(false)
    }
  }, [location.pathname, isRegistrationFormOpen, setIsRegistrationFormOpen])

  const handleClose = useCallback(() => {
    setIsRegistrationFormOpen(false)
  }, [setIsRegistrationFormOpen])

  if (!isMounted) {
    return null
  }

  return (
    <RegistrationSetupContextWrapper
      registration={registration}
      registrationTypes={registrationTypes}
      availableTaxes={availableTaxes}
      scheduleId={scheduleId}
      accountId={accountId}
      isRegistrationFormOpen={isRegistrationFormOpen}
    >
      <RegistrationSetupContent
        setIsRegistrationFormOpen={handleClose}
        refetchRegistrationFn={refetchRegistrationFn}
        refetchRegistrationInfoFn={refetchRegistrationInfoFn}
        isRegistrationFormOpen={isRegistrationFormOpen}
        registrationVersion={registrationVersion}
        isVisible={isVisible}
        transitionDuration={TRANSITION_DURATION}
      />
    </RegistrationSetupContextWrapper>
  )
}

export default RegistrationSetup
